import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { qaAnswerHtml } from '../../../../components/markdown/qaAnswerHtml';

const SessionQASection = ({
  questions = [],
  qaStatus = null,
  qaError = null,
  reportReady = true,
  isSubmitting = false,
  onRegenerate,
}) => {
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  // The question strings that currently have answers on the server. Used to
  // seed the editor and detect when a fresh set of answers has arrived.
  const answeredQuestions = useMemo(
    () => (Array.isArray(questions) ? questions.map((qa) => qa?.question || '') : []),
    [questions]
  );
  const answeredSignature = answeredQuestions.join('\u0001');

  const [questionInputs, setQuestionInputs] = useState(
    answeredQuestions.length > 0 ? answeredQuestions : ['']
  );

  // Re-seed the editor from the server questions whenever they change (initial
  // load and after a regeneration completes). While the user edits locally the
  // server list is unchanged, so their edits are preserved.
  useEffect(() => {
    setQuestionInputs(answeredQuestions.length > 0 ? answeredQuestions : ['']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answeredSignature]);

  const isProcessing = qaStatus === 'PROCESSING' || isSubmitting;
  const hasAnswers = Array.isArray(questions) && questions.length > 0;

  const updateQuestion = (index, value) => {
    setQuestionInputs((prev) => prev.map((q, i) => (i === index ? value : q)));
  };

  const addQuestion = () => {
    setQuestionInputs((prev) => [...prev, '']);
  };

  const removeQuestion = (index) => {
    setQuestionInputs((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [''];
    });
  };

  const handleSubmit = () => {
    const cleaned = questionInputs.map((q) => q.trim()).filter((q) => q.length > 0);
    if (cleaned.length === 0) return;
    onRegenerate?.(cleaned);
  };

  const cleanedCount = questionInputs.filter((q) => q.trim().length > 0).length;

  return (
    <div className="space-y-4">
      {/* Question editor */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Q&amp;A Analysis</h3>
            <p className="text-sm text-gray-500 mt-1">
              Add, edit or remove questions and regenerate only the Q&amp;A. The rest of the
              report stays unchanged.
            </p>
          </div>
          {isProcessing && (
            <span className="inline-flex items-center gap-1.5 text-sm text-indigo-600 whitespace-nowrap">
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              Generating…
            </span>
          )}
        </div>

        {!reportReady ? (
          <p className="text-sm text-gray-600">
            Generate the session report first, then you can run a Q&amp;A analysis.
          </p>
        ) : (
          <>
            <div className="space-y-2">
              {questionInputs.map((q, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <textarea
                    value={q}
                    onChange={(e) => updateQuestion(idx, e.target.value)}
                    rows={1}
                    className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 resize-y"
                    placeholder={`Question ${idx + 1}`}
                    disabled={isProcessing}
                  />
                  <button
                    type="button"
                    onClick={() => removeQuestion(idx)}
                    disabled={isProcessing}
                    className="mt-1 p-2 text-gray-400 hover:text-red-600 disabled:opacity-40"
                    title="Remove question"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-4">
              <button
                type="button"
                onClick={addQuestion}
                disabled={isProcessing}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                <PlusIcon className="h-4 w-4" />
                Add question
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isProcessing || cleanedCount === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                {isProcessing ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-4 w-4" />
                    {hasAnswers ? 'Regenerate Q&A' : 'Generate Q&A'}
                  </>
                )}
              </button>
            </div>

            {qaStatus === 'FAILED' && qaError && (
              <div className="mt-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                Q&amp;A generation failed: {qaError}
              </div>
            )}
          </>
        )}
      </div>

      {/* Answers */}
      {hasAnswers && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="space-y-3">
            {questions.map((qa, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedQuestion(expandedQuestion === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-900">{qa.question}</span>
                  {expandedQuestion === idx ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {expandedQuestion === idx && (
                  <div
                    className="px-4 pb-4 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: qaAnswerHtml(qa.answer) }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionQASection;
